import {
  getSkillReportByProgramCourseRefScope,
  getStudentPerformanceReport
} from "../../../../services/ready-services/report/reportQueryResolver"
import {
  ProgramAndCoursesInfo,
  SkillReportResponse,
  refScope
} from "../../../../types/gqlTypes/graphql"
import { PdfGeneratorGenerateOptions } from "../../../mp-shared-components/generic-shared-components/components"
import { StudentPerformanceDataBySubject } from "../../components/StudentPerformanceReportPdfMarkup/types"

export const getStudentPerformanceReportData = async (args: {
  contentId: string
  studentRefScope: refScope
  programsAndCourses: ProgramAndCoursesInfo[]
}): Promise<{
  program: string
  programDisplayName: string
  testName: string
  studentName: string
  isOverallAvailable: boolean
  overallScore: number
  overallMinScore: number
  overallMaxScore: number
  subtestsData: StudentPerformanceDataBySubject
} | null> => {
  const { contentId, studentRefScope, programsAndCourses } = args

  const reports = await getStudentPerformanceReport(
    [contentId],
    studentRefScope
  )

  const report = reports?.reports?.[0]?.reports?.[0]

  if (!report) {
    return null
  }

  const program = reports.reports[0].program
  const programData = programsAndCourses?.find(item => item.program === program)
  const programCourses = programData?.courses || []

  const testName = report.testName
  const studentName = report.studentName

  const isOverallAvailable = !!report.overallScore?.isAvailable
  const overallScore = isOverallAvailable
    ? report.overallScore?.score ?? null
    : null

  const subtestsReportData = report.subtests || []

  const skillReportBySubject = await Promise.all(
    subtestsReportData.map(item =>
      getSkillReportByProgramCourseRefScope({
        program,
        subject: item.subtestName,
        refScope: studentRefScope,
        contentIds: [contentId]
      })
    )
  ).then(response =>
    subtestsReportData.reduce(
      (res: Record<string, SkillReportResponse>, item, index) => ({
        ...res,
        [item.subtestName]: response?.[index] || null
      }),
      {}
    )
  )

  let overallMinScore = 0
  let overallMaxScore = 0

  const subtestsData: StudentPerformanceDataBySubject = programCourses?.reduce(
    (res, course) => {
      const reportData =
        subtestsReportData.find(item => item.subtestName === course.name) ||
        null

      if (!reportData) {
        return res
      }

      const skillReport = skillReportBySubject[course.name]

      const questionsAttempted = !!reportData
        ? reportData.items?.filter(item => !!item.answer)?.length || null
        : null

      overallMinScore = Math.min(overallMinScore, reportData?.minScore ?? 0)
      overallMaxScore = overallMaxScore + (reportData?.maxScore ?? 0)

      return {
        ...res,
        [course.name]: {
          ...(reportData || {}),
          questionsAttempted,
          skillReport,
          category: course.courseCategories?.at(-1) || "overall"
        }
      }
    },
    {}
  )

  return {
    program,
    programDisplayName: programData?.programInfo?.displayName || program,
    testName,
    studentName,
    isOverallAvailable,
    overallScore,
    overallMinScore,
    overallMaxScore,
    subtestsData
  }
}

export const getGeneratePdfOptions = async (args: {
  testName: string
  studentName: string
  programDisplayName: string
  subtestsData: StudentPerformanceDataBySubject
  isOverallAvailable: boolean
  overallScore: number
  overallMinScore: number
  overallMaxScore: number
}): Promise<PdfGeneratorGenerateOptions> => {
  const {
    testName,
    studentName,
    programDisplayName,
    subtestsData,
    isOverallAvailable,
    overallScore,
    overallMinScore,
    overallMaxScore
  } = args

  const {
    StudentPerformanceReportContentMarkup,
    StudentPerformanceReportHeaderMarkup
  } = await import("../../components/StudentPerformanceReportPdfMarkup")

  return {
    fileName: `${testName} ${studentName ?? "Student"} Performance Report.pdf`,
    documentTitle: testName,
    page: { margin: "0in" },
    header: {
      content: (
        <StudentPerformanceReportHeaderMarkup
          testName={testName}
          studentName={studentName}
        />
      ),
      marginSize: "0.6in"
    },
    content: (
      <StudentPerformanceReportContentMarkup
        program={programDisplayName}
        subtestsData={subtestsData}
        isOverallAvailable={isOverallAvailable}
        overallScore={overallScore}
        overallMinScore={overallMinScore}
        overallMaxScore={overallMaxScore}
      />
    )
  }
}
